<script lang="ts">
  import SalesActionDetailRow from './SalesActionDetailRow.svelte';
  import { needsContractCreation } from './contract-followup';
  import {
    buildRestoreConfirmMessage,
    inferRestoreTarget,
  } from './restore-target';
  import Modal from '@shared/ui/Modal.svelte';
  import {
    DISPLAY_STATUS_BADGE_CLS,
    DISPLAY_STATUS_LABELS,
    salesStatusToDisplay,
    type DisplayStatus,
  } from '@shared/constants/sales-action-display';
  import {
    LOST_REASON_LABELS,
    type SalesAction,
  } from '@shared/types/sales-action';
  import { VERIFICATION_STATUS_LABELS } from '@shared/types/customer-problem';
  import type { Lawyer } from '@domain/lawyer/model';

  interface SolutionOption {
    id: string;
    label: string;
  }

  interface Props {
    items: SalesAction[];
    lawyers: Lawyer[];
    solutions: SolutionOption[];
    canEdit?: boolean;
    /// 契約未作成 (won_contract_missing) 行の「契約を作成する」CTA。 未指定なら
    /// CTA を出さない (バッジ表示のみ)。
    onCreateContract?: (action: SalesAction) => void;
    savingId?: string | null;
    rowErrors?: Record<string, string | null>;
    /// 「ステータスを戻す」確定時に呼ぶ (API 呼び出し・タブ切替は親が担う)。
    restoreStatus?: (action: SalesAction) => void | Promise<void>;
  }

  let {
    items,
    lawyers,
    solutions,
    canEdit = false,
    onCreateContract,
    savingId = null,
    rowErrors = {},
    restoreStatus,
  }: Props = $props();

  let expandedId = $state<string | null>(null);

  // ── ステータスを戻す (誤操作救済の訂正操作) ──────────────────────────
  // 結果タブは読み取り専用のまま、 この訂正操作だけを詳細展開内に出す
  // (仕様: 20260612-sales-action-result-status-restore)。
  let restoreTargetAction = $state<SalesAction | null>(null);
  const restoreTargetTo = $derived(
    restoreTargetAction ? inferRestoreTarget(restoreTargetAction) : null,
  );

  function openRestoreConfirm(action: SalesAction) {
    restoreTargetAction = action;
  }

  function cancelRestore() {
    restoreTargetAction = null;
  }

  async function confirmRestore() {
    const action = restoreTargetAction;
    restoreTargetAction = null;
    if (!action) return;
    await restoreStatus?.(action);
  }

  const lawyerNameById = $derived(
    Object.fromEntries(lawyers.map((l) => [l.id, l.full_name])),
  );

  $effect(() => {
    if (expandedId && !items.some((action) => action.id === expandedId)) {
      expandedId = null;
    }
  });

  function participantNames(action: SalesAction): string {
    return action.participant_lawyer_ids
      .map((id) => lawyerNameById[id] ?? id)
      .join('、 ');
  }

  // 課題の表示値は一覧応答同梱の source_problem を正とする (86eya0dva PR3)。
  // 画面ターゲット単位の customer_problems 取得では、混在行 (別ターゲットの課題を
  // 参照する行) を解決できない。
  function problemLabel(action: SalesAction): string {
    return action.source_problem?.problem_name ?? '(名称不明の課題)';
  }

  function aiScore(action: SalesAction): string {
    const score = action.source_problem?.ai_score;
    return score == null ? '—' : String(score);
  }

  function verificationLabel(action: SalesAction): string {
    const status = action.source_problem?.verification_status;
    if (!status) return '—';
    return VERIFICATION_STATUS_LABELS[status] ?? status;
  }

  function solutionLabel(id: string | null): string {
    if (!id) return '未設定';
    return solutions.find((s) => s.id === id)?.label ?? id;
  }

  function displayStatusOf(action: SalesAction): DisplayStatus {
    return salesStatusToDisplay(action.sales_status);
  }

  function lostReasonLabel(action: SalesAction): string {
    if (action.sales_status !== 'lost' || !action.lost_reason) return '—';
    return LOST_REASON_LABELS[action.lost_reason] ?? action.lost_reason;
  }

  function resultLabel(action: SalesAction): string {
    // 受注/失注のソリューションは独立した「ソリューション」列で視認するため、
    // 結果列は失注理由のみを担う (受注行は —)。
    return action.sales_status === 'lost' ? lostReasonLabel(action) : '—';
  }

  function optionalText(value: string | null): string {
    const text = value?.trim();
    return text ? text : '—';
  }

  function lostReasonNoteText(action: SalesAction): string {
    if (action.sales_status !== 'lost' || !action.lost_reason_note) return '—';
    return action.lost_reason_note;
  }

  function completedDate(action: SalesAction): string {
    if (!action.completed_at) return '—';
    return action.completed_at.slice(0, 10);
  }

  function detailPanelId(action: SalesAction): string {
    return `sales-action-result-detail-${action.id}`;
  }

  function detailToggleId(action: SalesAction): string {
    return `sales-action-result-detail-toggle-${action.id}`;
  }

  function toggleDetail(action: SalesAction) {
    expandedId = expandedId === action.id ? null : action.id;
  }
</script>

<!-- 結果: 受注/失注の確定済みアクション (読み取り専用)。
     例外として、 誤操作救済の「ステータスを戻す」訂正操作だけを詳細展開内に出す。 -->
<div class="table-scroll">
  <table>
    <thead>
      <tr>
        <th class="col-status">ステータス</th>
        <th class="col-completed">完了日</th>
        <th class="col-solution">ソリューション</th>
        <th class="col-result">結果</th>
        <th class="col-participants">対象</th>
        <th class="col-actions">操作</th>
      </tr>
    </thead>
    <tbody>
      {#each items as action (action.id)}
        {@const focal = action.source_problem ?? null}
        {@const ds = displayStatusOf(action)}
        {@const participants = participantNames(action)}
        {@const actionTitle = optionalText(action.action_title)}
        {@const note = optionalText(action.note)}
        {@const lostNote = lostReasonNoteText(action)}
        {@const expanded = expandedId === action.id}
        {@const restoreTo = inferRestoreTarget(action)}
        <tr
          data-testid="sales-action-result-row"
          data-display-status={ds}
        >
          <td class="col-status">
            <span class="status-chip {DISPLAY_STATUS_BADGE_CLS[ds]}">
              {DISPLAY_STATUS_LABELS[ds]}
            </span>
          </td>
          <td class="col-completed">{completedDate(action)}</td>
          <td class="col-solution" title={solutionLabel(action.product_id)}>
            {solutionLabel(action.product_id)}
          </td>
          <td
            class="col-result"
            title={needsContractCreation(action)
              ? '契約未作成'
              : resultLabel(action)}
          >
            {#if needsContractCreation(action)}
              <span
                class="contract-missing-chip"
                data-testid="sales-action-contract-missing"
              >
                契約未作成
              </span>
            {:else}
              {resultLabel(action)}
            {/if}
          </td>
          <td class="col-participants" title={participants || undefined}>
            {#if action.participant_lawyer_ids.length > 0}
              {participants}
            {:else}
              <span class="muted">—</span>
            {/if}
          </td>
          <td class="col-actions">
            <button
              type="button"
              id={detailToggleId(action)}
              class="detail-toggle"
              data-testid="sales-action-detail-toggle"
              aria-expanded={expanded}
              aria-controls={detailPanelId(action)}
              onclick={() => toggleDetail(action)}
            >
              <span aria-hidden="true">{expanded ? '▴' : '▾'}</span>
              詳細
            </button>
          </td>
        </tr>
        {#if expanded}
          <SalesActionDetailRow
            colspan={6}
            id={detailPanelId(action)}
            labelledby={detailToggleId(action)}
          >
            <div class="detail-grid">
              <div class="detail-field detail-field-full">
                <span class="detail-label">次アクション詳細</span>
                <span
                  class="detail-value detail-note"
                  class:muted={actionTitle === '—'}
                  title={actionTitle === '—' ? undefined : actionTitle}
                >
                  {actionTitle}
                </span>
              </div>

              <div class="detail-problem-row">
                <div class="detail-field">
                  <span class="detail-label">課題</span>
                  <span class="detail-value">
                    {#if focal}
                      <span
                        class="issue-tag"
                        class:denied={focal.verification_status === 'denied'}
                        title={problemLabel(action)}
                      >
                        {problemLabel(action)}
                      </span>
                    {:else}
                      <span class="muted">—</span>
                    {/if}
                  </span>
                </div>
                <div class="detail-field">
                  <span class="detail-label">AI推認スコア</span>
                  <span class="detail-value">{aiScore(action)}</span>
                </div>
                <div class="detail-field">
                  <span class="detail-label">確認状態</span>
                  <span class="detail-value">{verificationLabel(action)}</span>
                </div>
              </div>

              <div class="detail-field">
                <span class="detail-label">ソリューション</span>
                <span class="detail-value">
                  {solutionLabel(action.product_id)}
                </span>
              </div>
              {#if action.sales_status === 'won'}
                <div class="detail-field">
                  <span class="detail-label">契約</span>
                  <span class="detail-value contract-cell">
                    {#if needsContractCreation(action)}
                      未作成
                      {#if onCreateContract}
                        <button
                          type="button"
                          class="create-contract-btn"
                          data-testid="sales-action-create-contract"
                          disabled={!canEdit}
                          onclick={() => onCreateContract(action)}
                        >
                          契約を作成する
                        </button>
                      {/if}
                    {:else}
                      作成済
                      {#if action.contract_party?.is_individual_display}
                        <span
                          class="individual-badge"
                          data-testid="sales-action-contract-individual"
                        >
                          個人契約
                        </span>
                      {/if}
                    {/if}
                  </span>
                </div>
              {/if}
              <div class="detail-field detail-field-full">
                <span class="detail-label">メモ</span>
                <span
                  class="detail-value detail-note"
                  class:muted={note === '—'}
                  title={note === '—' ? undefined : note}
                >
                  {note}
                </span>
              </div>
              {#if action.sales_status === 'lost'}
                <div class="detail-field">
                  <span class="detail-label">失注理由</span>
                  <span class="detail-value">{lostReasonLabel(action)}</span>
                </div>
                <div class="detail-field detail-field-wide">
                  <span class="detail-label">失注理由メモ</span>
                  <span
                    class="detail-value detail-note"
                    title={lostNote === '—' ? undefined : lostNote}
                  >
                    {lostNote}
                  </span>
                </div>
              {/if}
              {#if canEdit && restoreTo !== null}
                <!-- 誤操作救済の訂正操作。 戻し先を履歴から推定できる結果行のみ表示。 -->
                <div class="detail-field detail-field-full restore-field">
                  <button
                    type="button"
                    class="restore-button"
                    data-testid="sales-action-restore-status"
                    disabled={savingId === action.id}
                    onclick={() => openRestoreConfirm(action)}
                  >
                    ステータスを戻す
                  </button>
                </div>
              {/if}
            </div>
          </SalesActionDetailRow>
        {/if}
        {#if rowErrors[action.id]}
          <tr class="row-error">
            <td colspan="6">保存に失敗しました: {rowErrors[action.id]}</td>
          </tr>
        {/if}
      {/each}
    </tbody>
  </table>
</div>

{#if restoreTargetAction && restoreTargetTo !== null}
  <Modal open={true} title="ステータスを戻す" onClose={cancelRestore}>
    <p class="restore-message" data-testid="restore-status-message">
      {buildRestoreConfirmMessage(restoreTargetAction, restoreTargetTo)}
    </p>
    {#snippet footer()}
      <button type="button" class="dialog-button" onclick={cancelRestore}>
        キャンセル
      </button>
      <button
        type="button"
        class="dialog-button primary"
        data-testid="restore-status-confirm"
        onclick={confirmRestore}
      >
        戻す
      </button>
    {/snippet}
  </Modal>
{/if}

<style>
  .issue-tag {
    display: inline-block;
    padding: 2px 8px;
    background: #eef2ff;
    color: #3730a3;
    border-radius: 999px;
    white-space: nowrap;
  }
  .issue-tag.denied {
    background: #f3f4f6;
    color: #9ca3af;
    text-decoration: line-through;
  }
  .table-scroll {
    align-self: stretch;
    width: 100%;
    overflow-x: hidden;
    overflow-y: visible;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
    table-layout: fixed;
  }
  th,
  td {
    padding: 6px 8px;
    text-align: left;
    border-bottom: 1px solid #e5e7eb;
    vertical-align: top;
  }
  th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f9fafb;
    color: #374151;
    font-weight: 600;
  }
  .col-status {
    width: 104px;
  }
  .col-completed {
    width: 112px;
  }
  .col-solution {
    width: 24%;
  }
  .col-result {
    width: 20%;
  }
  .col-participants {
    width: 24%;
  }
  .col-actions {
    width: 104px;
    text-align: right;
  }
  td.col-solution,
  td.col-result,
  td.col-participants {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .status-chip {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
  }
  .status-no-contact {
    background: #f3f4f6;
    color: #4b5563;
  }
  .status-contacted {
    background: #dbeafe;
    color: #1e40af;
  }
  .status-in-dialogue {
    background: #e0f2fe;
    color: #075985;
  }
  .status-appointment {
    background: #fef3c7;
    color: #92400e;
  }
  .status-negotiation-c {
    background: #ffedd5;
    color: #9a3412;
  }
  .status-negotiation-b {
    background: #dcfce7;
    color: #166534;
  }
  .status-negotiation-a {
    background: #bbf7d0;
    color: #166534;
  }
  .status-won {
    background: #dcfce7;
    color: #166534;
  }
  .status-lost {
    background: #fee2e2;
    color: #991b1b;
  }
  .contract-missing-chip {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
    background: #fef3c7;
    color: #92400e;
  }
  .individual-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
    background: #fdf2f8;
    color: #9d174d;
  }
  .contract-cell {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .create-contract-btn {
    padding: 4px 12px;
    border: 1px solid #2563eb;
    border-radius: 4px;
    background: #2563eb;
    color: white;
    cursor: pointer;
    font: inherit;
    font-size: 0.8125rem;
    white-space: nowrap;
  }
  .create-contract-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .detail-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    min-width: 72px;
    padding: 4px 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background: white;
    color: #374151;
    cursor: pointer;
    font: inherit;
    white-space: nowrap;
  }
  .detail-toggle:hover {
    background: #f9fafb;
  }
  .detail-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px 16px;
  }
  .detail-problem-row {
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px 16px;
  }
  .detail-field {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .detail-field-full {
    grid-column: 1 / -1;
  }
  .detail-field-wide {
    grid-column: span 2;
  }
  .detail-label {
    color: #6b7280;
    font-size: 0.75rem;
    font-weight: 600;
  }
  .detail-value {
    min-width: 0;
    color: #111827;
    overflow-wrap: anywhere;
  }
  .detail-note {
    white-space: pre-wrap;
  }
  .restore-field {
    flex-direction: row;
    justify-content: flex-end;
  }
  .restore-button {
    padding: 4px 12px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background: white;
    color: #374151;
    cursor: pointer;
    font: inherit;
    white-space: nowrap;
  }
  .restore-button:hover {
    background: #f9fafb;
  }
  .restore-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .row-error td {
    padding: 4px 8px;
    color: #b91c1c;
    font-size: 0.8125rem;
    background: #fef2f2;
  }
  .restore-message {
    margin: 0;
    white-space: pre-line;
  }
  .dialog-button {
    padding: 8px 16px;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    font: inherit;
  }
  .dialog-button.primary {
    background: #2563eb;
    border-color: #2563eb;
    color: white;
  }
  .muted {
    color: #6b7280;
  }

  @media (max-width: 900px) {
    .col-solution {
      width: 22%;
    }
    .col-result {
      width: 20%;
    }
    .col-participants {
      width: 22%;
    }
    .detail-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .detail-problem-row {
      grid-template-columns: 1fr;
    }
  }
</style>
